import { testColumns } from '../test/testColumns';
import type { UserFlowType } from '~/server/api/routers/userflow';
import type { RouterOutputs } from '~/trpc-extra/api';
import { ActionIcon, Menu, CopyButton } from '@mantine/core';
import { IconDots, IconExternalLink, IconStrikethrough, IconTrash, IconClipboardCopy } from '@tabler/icons-react';
import { useRouter } from 'next/router';
import { useProjectId } from '~/hooks/context/useProjectId';
import type { ColumnDef, Row } from '@tanstack/react-table';
import type { TestListItem } from '~/server/api/routers/test';

export const testSuiteTestsColumns = ({
  userFlows,
  tags,
  handleSkipTests,
  handleRemoveTestsFromSuite,
  tests,
}: {
  userFlows: UserFlowType[];
  tags: RouterOutputs['tag']['list'];
  handleSkipTests: (testIds: string[], skip: boolean) => void;
  handleRemoveTestsFromSuite: (testIds: string[]) => void;
  tests: TestListItem[];
}): ColumnDef<TestListItem>[] => {
  return [
    ...testColumns({ userFlows, tags, tests }).filter((col) => col.id !== 'actions'),
    {
      id: 'actions',
      header: () => <div role="none" />,
      cell: ({ row }) => (
        <ActionsCell
          row={row}
          handleSkipTests={handleSkipTests}
          handleRemoveTestsFromSuite={handleRemoveTestsFromSuite}
        />
      ),
    },
  ];
};

function ActionsCell({
  row,
  handleSkipTests,
  handleRemoveTestsFromSuite,
}: {
  row: Row<TestListItem>;
  handleSkipTests: (testIds: string[], skip: boolean) => void;
  handleRemoveTestsFromSuite: (testIds: string[]) => void;
}) {
  const router = useRouter();
  const projectId = useProjectId();

  return (
    <Menu
      shadow="md"
      transitionProps={{ duration: 0 }}
    >
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={(e) => e.stopPropagation()}
        >
          <IconDots size={14} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
        <Menu.Label>Quick Actions</Menu.Label>
        <Menu.Item
          onClick={() => router.push(`/project/${projectId}/test/${row.original.id}`)}
          leftSection={<IconExternalLink size={14} />}
        >
          Open
        </Menu.Item>
        <Menu.Item
          onClick={() => handleSkipTests([row.original.id], !row.original.skip)}
          leftSection={<IconStrikethrough size={14} />}
        >
          {row.original.skip ? 'Unskip' : 'Skip'}
        </Menu.Item>
        <CopyButton value={row.original.id}>
          {({ copy }) => (
            <Menu.Item
              leftSection={<IconClipboardCopy size={14} />}
              onClick={copy}
            >
              Copy ID
            </Menu.Item>
          )}
        </CopyButton>
        <Menu.Divider />
        <Menu.Item
          leftSection={<IconTrash size={14} />}
          color="red"
          onClick={() => handleRemoveTestsFromSuite([row.original.id])}
        >
          Remove From Suite
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
